'use client';

import { motion } from 'framer-motion';

import Button from 'components/ui/Button';
import { fadeUpVariant, staggerContainer, staggerItem } from 'lib/utils';
import PeanutParticles from 'components/ui/PeanutParticles';
import heroPackImg from '../../images/product/ProductImg.webp';

export default function Hero() {
  return (
    <section className="relative min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-5rem)] flex items-center justify-center overflow-hidden bg-[#Fdfbf7] py-10 sm:py-14 lg:py-20">
      {/* Background gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-singaraa-cream/50 via-[#f5eedc]/30 to-singaraa-caramel/10 z-0 pointer-events-none" />
      <PeanutParticles />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-10 sm:gap-12 lg:gap-20">

          {/* Text Content */}
          <motion.div
            className="flex-1 w-full flex flex-col items-center lg:items-start text-center lg:text-left order-2 lg:order-1 mt-4 lg:mt-0"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.span
              variants={staggerItem}
              className="inline-block uppercase tracking-[0.18em] sm:tracking-[0.2em] text-singaraa-caramel font-semibold text-xs sm:text-sm mb-3 sm:mb-6"
            >
              The Taste of Home
            </motion.span>

            <motion.h1
              variants={fadeUpVariant}
              className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-6xl text-singaraa-dark leading-[1.15] mb-4 sm:mb-6 break-words"
            >
              A NEW STANDARD OF SNACKING<br />
              <span className="text-singaraa-brown italic">Roasted for Today.</span>
            </motion.h1>

            <motion.p
              variants={fadeUpVariant}
              className="font-body text-singaraa-dark/80 text-base sm:text-lg md:text-xl max-w-xl mb-6 sm:mb-10 leading-relaxed"
            >
              Experience the perfect crunch with our premium dry roasted peanuts.
              Sourced directly from the finest farms, bringing warmth and flavor to your everyday moments.
            </motion.p>

            <motion.div
              variants={fadeUpVariant}
              className="flex flex-col sm:flex-row flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-3.5 w-full sm:w-auto"
            >
              <Button href="#order-now" variant="whatsapp" size="md" className="w-full sm:w-auto">
                Order Now
              </Button>

              <Button href="/product" variant="outline" size="md" className="w-full sm:w-auto">
                Explore Product
              </Button>
            </motion.div>
          </motion.div>

          {/* Visual Content */}
          <motion.div
            className="flex-1 w-full relative min-h-[320px] sm:min-h-[420px] lg:min-h-[550px] flex justify-center items-center py-4 order-1 lg:order-2"
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Abstract background shapes */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-singaraa-caramel/10 rounded-full blur-3xl -z-10" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/3 -translate-y-2/3 w-[80%] h-[80%] bg-singaraa-gold/20 rounded-full blur-2xl -z-10" />

            {/* Product Image Showcase */}
            <div className="relative w-full max-w-[280px] sm:max-w-sm md:max-w-md z-10 group">
              <div className="w-full h-full flex items-center justify-center relative p-2 sm:p-4">
                <img
                  src={heroPackImg}
                  alt="Singaraa Premium Roasted Peanuts"
                  className="w-full h-full object-contain m-0 drop-shadow-2xl z-10 transition-transform duration-700 ease-out group-hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>

              {/* Floating Quality Badges */}
              <div className="absolute -top-2.5 left-1.5 sm:-top-3 sm:-left-3 md:-left-4 bg-white/95 backdrop-blur-md px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl sm:rounded-2xl shadow-lg border border-singaraa-caramel/20 z-20 flex items-center gap-1.5 sm:gap-2">
                <span className="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-[#25D366] animate-pulse" />
                <span className="text-[11px] sm:text-xs font-bold text-singaraa-dark font-heading whitespace-nowrap">100% Dry Roasted</span>
              </div>

              <div className="absolute -bottom-2.5 right-1.5 sm:-bottom-3 sm:-right-3 md:-right-4 bg-singaraa-brown text-white px-3.5 py-1.5 sm:px-5 sm:py-2 rounded-xl sm:rounded-2xl shadow-xl border border-singaraa-caramel/30 z-20 flex items-center gap-1.5 sm:gap-2">
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider font-body whitespace-nowrap">500g Fresh Pack</span>
              </div>
            </div>

            {/* Smooth Ambient Floating Elements */}
            <motion.div
              animate={{ y: [0, -12, 0], rotate: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
              className="absolute top-1/4 right-0 w-12 sm:w-16 h-12 sm:h-16 bg-singaraa-brown/20 rounded-full blur-md z-20 pointer-events-none"
            />
            <motion.div
              animate={{ y: [0, 16, 0], rotate: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 10, ease: "easeInOut", delay: 1 }}
              className="absolute bottom-1/4 left-0 w-16 sm:w-24 h-16 sm:h-24 bg-singaraa-caramel/30 rounded-full blur-lg z-0 pointer-events-none"
            />
          </motion.div>

        </div>
      </div>
    </section>
  );
}
